<template>
  <!--
    «Реквизиты для оплаты» (CONCIERGE_CHAT_FIRST.md §5.2, §5.7): the payment this card belongs to, read from its state.
    Fiat — «К оплате», the requisites the manager released, «Я отправил средства», «Запросить возврат»; crypto — the
    wallet's phase (conciergeCrypto.ts): amount, address with its QR, deadline, «Получить новый расчёт», «Вернуть» with
    the refund address. A payment that is no longer the current one — «Реквизиты заменены» (or, refunded, «Оплата
    отменена, средства возвращены»), nothing else.
    The design's requisites modal (the `.summary` total, `.detail-row` with a copy button) as a `.case-terms` card.
  -->
  <div class="cb" :class="{ 'cb--muted': state?.superseded }" data-testid="chat-block-CONCIERGE_PAY_IN">
    <div class="cb__title" data-testid="concierge-payin-title">{{ t(w('vitrine.concierge.payin.title')) }}</div>
    <div v-if="state?.superseded" class="cb__state" data-testid="chat-block-state">{{ t(supersededKey(state)) }}</div>
    <p v-else-if="!p" class="cb__text">{{ t('vitrine.concierge.event.PAY_IN') }}</p>

    <!-- Crypto: driven only by `payment.wallet` (the server's ClientWalletView). -->
    <div v-else-if="crypto" class="cb__body" data-testid="concierge-crypto">
      <div class="cb__row cb__summary" data-testid="concierge-crypto-ask">
        <span>{{ t('vitrine.concierge.payment.toPay') }}</span>
        <strong v-if="crypto.ask" class="num">
          {{ f.toPay(crypto.ask.amount, crypto.ask.asset ?? 'USDT') }} {{ crypto.ask.asset }}
          <small v-if="crypto.ask.feePercent != null" class="cb__rowSub" data-testid="concierge-crypto-fee">
            {{ crypto.ask.fee != null
              ? t('vitrine.concierge.wallet.feeIncludedAmount', { percent: f.pct(crypto.ask.feePercent), amount: f.token(crypto.ask.fee, crypto.ask.asset) + ' ' + crypto.ask.asset })
              : t('vitrine.concierge.payment.feeIncluded', { percent: f.pct(crypto.ask.feePercent) }) }}
          </small>
        </strong>
        <span v-else class="cb__soft">{{ t('vitrine.concierge.wallet.askPending') }}</span>
      </div>
      <div v-if="p.payoutAmount != null" class="cb__row">
        <small>{{ t('vitrine.concierge.terms.recipientGets') }}</small>
        <span class="num">{{ f.money(p.payoutAmount, p.payoutCurrency) }} {{ p.payoutCurrency }}</span>
      </div>
      <div v-if="crypto.deadline" class="cb__row" data-testid="concierge-crypto-deadline">
        <small>{{ t('vitrine.concierge.payment.deadline') }}</small>
        <span>{{ f.dateTime(crypto.deadline) }}</span>
      </div>
      <div v-if="crypto.received != null" class="cb__row" data-testid="concierge-crypto-received">
        <small>{{ t('vitrine.concierge.wallet.received') }}</small>
        <span class="num">{{ f.token(crypto.received, p.wallet?.asset) }} {{ p.wallet?.asset }}</span>
      </div>
      <div v-if="p.paymentId" class="cb__row" data-testid="concierge-payment-id">
        <small>{{ t('vitrine.concierge.payment.paymentId') }}</small>
        <span class="num">{{ p.paymentId }}</span>
      </div>

      <div v-if="crypto.titleKey" class="cb__notice" :data-phase="crypto.phase" data-testid="concierge-crypto-phase">
        <div class="cb__noticeTitle">{{ t(crypto.titleKey, cryptoTitleParams) }}</div>
        <div v-if="crypto.bodyKey" class="cb__noticeBody">{{ t(crypto.bodyKey) }}</div>
      </div>

      <!-- The call to send money — only while the server asks for it (AWAITING with a frozen address). -->
      <div v-if="crypto.address && p.wallet" class="cb__detail" data-testid="concierge-crypto-address">
        <div class="cb__detailHead">
          <small>{{ t('vitrine.concierge.wallet.addressTitle') }}</small>
          <small class="num">{{ p.wallet.asset }} · {{ p.wallet.network }}</small>
        </div>
        <DepositAddressCall :address="crypto.address" :asset="p.wallet.asset" :network="p.wallet.network"
                            testid-prefix="concierge" @copied="copied = true" />
      </div>

      <div v-if="p.onHold && !refundRunning" class="cb__notice" data-testid="concierge-payment-on-hold">
        <div class="cb__noticeTitle">{{ t('vitrine.concierge.payment.onHold') }}</div>
      </div>

      <button v-if="crypto.requote" type="button" class="cb__primary" :disabled="disabled"
              data-testid="concierge-crypto-requote" @click="requote">{{ t('vitrine.concierge.wallet.requote') }}</button>
      <button v-if="crypto.refundRequest && !refundFormOpen" type="button" class="cb__secondary cb__secondary--wide"
              :disabled="disabled" data-testid="concierge-crypto-refund" @click="refundFormOpen = true">
        {{ t('vitrine.concierge.wallet.refund') }}
      </button>
      <!-- «Вернуть»: the address is optional — by default the money goes back to the sender. -->
      <div v-if="crypto.refundRequest && refundFormOpen" class="cb__form" data-testid="concierge-crypto-refund-form">
        <div class="cb__noticeTitle">{{ t('vitrine.concierge.wallet.refundTitle') }}</div>
        <div class="cb__label">{{ t('vitrine.concierge.wallet.refundAddress') }}</div>
        <input v-model="refundAddress" class="cb__input" maxlength="128" autocomplete="off" autocapitalize="off"
               spellcheck="false" data-testid="concierge-crypto-refund-address"
               :placeholder="t('vitrine.concierge.wallet.refundAddressPlaceholder', { network: p.wallet?.network ?? '' })">
        <p class="cb__hint">{{ t('vitrine.concierge.wallet.refundAddressHint') }}</p>
        <div class="cb__label">{{ t('vitrine.topup.comment') }}</div>
        <input v-model="refundComment" class="cb__input" maxlength="500" data-testid="concierge-crypto-refund-comment"
               :placeholder="t('vitrine.topup.commentPlaceholder')">
        <button type="button" class="cb__primary" :disabled="disabled" data-testid="concierge-crypto-refund-send"
                @click="sendCryptoRefund">{{ t('vitrine.concierge.wallet.refundSend') }}</button>
        <button type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled"
                @click="refundFormOpen = false">{{ t('common.cancel') }}</button>
      </div>
      <!-- The provider holds the money (AT_TH): the same «Запросить возврат» as the fiat payment (PayFlow T29). -->
      <button v-if="actions?.refund" type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled"
              data-testid="concierge-request-refund" @click="requestRefund">{{ t('vitrine.topup.requestRefund') }}</button>
      <p v-if="crypto.refundRequested" class="cb__hint" data-testid="concierge-refund-requested">
        {{ t('vitrine.concierge.payment.refundRequested') }}
      </p>
    </div>

    <!-- Fiat (bank / cash). Cash reads in its own words (w): a place and a time to hand the notes over. -->
    <template v-else>
      <div class="cb__row cb__summary" data-testid="concierge-payment-ask">
        <span>{{ t(w('vitrine.concierge.payment.toPay')) }}</span>
        <strong v-if="ask" class="num">
          {{ f.toPay(ask.amount, ask.currency) }} {{ ask.currency }}
          <small v-if="ask.feePercent != null" class="cb__rowSub" data-testid="concierge-payment-fee">
            {{ t('vitrine.concierge.payment.feeIncluded', { percent: f.pct(ask.feePercent) }) }}
          </small>
        </strong>
        <span v-else class="cb__soft" data-testid="concierge-payment-ask-pending">{{ t('vitrine.concierge.payment.askPending') }}</span>
      </div>
      <div v-if="p.payoutAmount != null" class="cb__row">
        <small>{{ t('vitrine.concierge.terms.recipientGets') }}</small>
        <span class="num">{{ f.money(p.payoutAmount, p.payoutCurrency) }} {{ p.payoutCurrency }}</span>
      </div>
      <!-- A concierge payment has no pay-by date of its own: the row only when TH sent one (ConciergePaymentMirror),
           never a «the manager will tell» placeholder (walkthrough К9). -->
      <div v-if="awaitsFunding(p) && p.fundingDeadline" class="cb__row" data-testid="concierge-payment-deadline">
        <small>{{ t(w('vitrine.concierge.payment.deadline')) }}</small>
        <span>{{ f.date(p.fundingDeadline) }}</span>
      </div>
      <div v-if="p.paymentId" class="cb__row" data-testid="concierge-payment-id">
        <small>{{ t('vitrine.concierge.payment.paymentId') }}</small>
        <span class="num">{{ p.paymentId }}</span>
      </div>

      <!-- The requisites a manager released — markdown authored in the BO, the escape-first sanitizer of the top-up
           screen. Never raw HTML: this text originates outside Tacita. -->
      <div v-if="p.payInInstructions" class="cb__detail" data-testid="concierge-payin">
        <div class="cb__detailHead">
          <!-- Not the top-up's «Реквизиты для перевода»: no «перевод» in concierge texts (spec §7, walkthrough К6). -->
          <small>{{ t(w('vitrine.concierge.payin.details')) }}</small>
          <button type="button" class="cb__copy" data-testid="concierge-payin-copy" @click="copyPayIn">
            <IconCopy :size="14" />{{ payInCopied ? t('common.copied') : t('vitrine.topup.payin.copy') }}
          </button>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -- sanitized by renderMarkdown (escape-first, XSS-hardened) -->
        <div class="cb__detailBody" v-html="payInHtml" />
      </div>
      <p v-else-if="awaitsPayIn(p)" class="cb__hint" data-testid="concierge-payin-pending">
        {{ t(w('vitrine.concierge.payment.payInByManager')) }}
      </p>

      <!-- A refund under way has its own line in the feed: «on hold» and «marked sent» would contradict it. -->
      <div v-if="p.onHold && !refundRunning" class="cb__notice" data-testid="concierge-payment-on-hold">
        <div class="cb__noticeTitle">{{ t('vitrine.concierge.payment.onHold') }}</div>
      </div>

      <button v-if="actions?.markSent" type="button" class="cb__primary" :disabled="disabled"
              data-testid="concierge-mark-sent" @click="markSent">{{ t(w('vitrine.topup.iSent')) }}</button>
      <p v-if="p.markedSent && !refundRunning" class="cb__hint" data-testid="concierge-marked-sent">
        {{ t(w('vitrine.concierge.payment.markedSent')) }}
      </p>
      <!-- The payment service already holds the money — the client can only ask for a refund (PayFlow T29). -->
      <button v-if="actions?.refund" type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled"
              data-testid="concierge-request-refund" @click="requestRefund">{{ t('vitrine.topup.requestRefund') }}</button>
      <p v-if="actions?.refundRequested" class="cb__hint" data-testid="concierge-refund-requested">
        {{ t('vitrine.concierge.payment.refundRequested') }}
      </p>
    </template>

    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
    <Toast v-model:open="copied" :message="t('common.copied')" data-testid="concierge-copied-toast" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Toast from '@/components/ui/feedback/Toast.vue';
import DepositAddressCall from '@/components/vitrine/DepositAddressCall.vue';
import { IconCopy } from '@/components/ui/icons';
import { requestConciergeCryptoRefund, requoteConciergePayment, type PaymentClientView } from '@/api/concierge';
import { markTopUpSent, requestTopUpRefund } from '@/api/topup';
import type { BlockState } from '@/api/support';
import { supersededKey } from '../chatBlocks';
import { renderMarkdown } from '@/utils/markdown';
import { askOf, awaitsFunding, awaitsPayIn, paymentActions } from '@/views/vitrine/concierge/conciergeUi';
import { cryptoBlock } from '@/views/vitrine/concierge/conciergeCrypto';
import { forMethod } from '@/views/vitrine/topupStatus';
import { useCardAction, useCardFormat } from './useCardAction';

const props = defineProps<{
  state?: Extract<BlockState, { type: 'CONCIERGE_PAY_IN' }>;
  /** The concierge request of this thread; null — the screen has not loaded it yet (no call can go). */
  requestId: string | null;
  /** The request's shown status: REFUND_PENDING already says «средства возвращаются» elsewhere. */
  taskStatus?: string | null;
  busy?: boolean;
  reread: () => Promise<void> | void;
}>();

const { t } = useI18n();
const f = useCardFormat();
const { running, error, run } = useCardAction(() => props.reread());

const p = computed<PaymentClientView | null>(() => (props.state && !props.state.superseded ? props.state.payment : null));
/** The key in the payment's own words: the cash counterpart for a cash payment (topupStatus.forMethod). */
const w = (key: string): string => forMethod(key, props.state?.payment?.sourceKind);
const disabled = computed(() => !!props.busy || running.value);
const ask = computed(() => askOf(p.value));
const actions = computed(() => (p.value ? paymentActions(p.value) : null));
/** A refund of this payment is under way or done (§6.6): its line replaces «on hold» / «marked sent». */
const refundRunning = computed(() => (p.value?.refundState ?? 'NONE') !== 'NONE');
const payInHtml = computed(() => renderMarkdown(p.value?.payInInstructions ?? ''));
const crypto = computed(() => (p.value ? cryptoBlock(p.value, props.taskStatus) : null));
const cryptoTitleParams = computed(() => {
  const x = crypto.value?.titleParams ?? {};
  return {
    // «Отправьте N» / «Доплатите N» — an amount to pay: rounded up (review M5).
    amount: x.amount != null ? f.toPay(x.amount, x.asset ?? 'USDT') : '',
    asset: x.asset ?? '',
    date: x.date ? f.dateTime(x.date) : '',
  };
});

const copied = ref(false);
const payInCopied = ref(false);
const refundFormOpen = ref(false);
const refundAddress = ref('');
const refundComment = ref('');
// the refund form closes once «Вернуть» is no longer offered (sent, or the payment moved on)
watch(() => crypto.value?.refundRequest, (on) => { if (!on) refundFormOpen.value = false; });

function markSent() {
  const id = p.value?.id;
  if (id) void run(() => markTopUpSent(id));
}

function requestRefund() {
  const id = p.value?.id;
  if (id) void run(() => requestTopUpRefund(id));
}

/** Crypto «Получить новый расчёт» (wallet EXPIRED): a new amount and deadline on the same address. */
function requote() {
  const id = props.requestId;
  if (id) void run(() => requoteConciergePayment(id));
}

/** Crypto «Вернуть» before the forward: the address is optional — by default the money returns to the sender. */
function sendCryptoRefund() {
  const id = props.requestId;
  if (!id) return;
  void run(() => requestConciergeCryptoRefund(id, { refundAddress: refundAddress.value, comment: refundComment.value }))
    .then((ok) => { if (ok) { refundAddress.value = ''; refundComment.value = ''; } });
}

/** Requisites are long and error-prone to retype — a mistyped IBAN is a lost payment. */
async function copyPayIn() {
  const text = p.value?.payInInstructions;
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    payInCopied.value = true;
    setTimeout(() => { payInCopied.value = false; }, 2000);
  } catch { /* clipboard denied — the text is on screen and selectable anyway */ }
}
</script>

<style scoped lang="scss">
@use './cards.scss';
</style>
